'use client'

import { Copy, X } from 'lucide-react'
import Image from 'next/image'
import { type ReactNode, useEffect, useState } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { buildShareUrl } from '@/helpers/build-share-url'

interface ShareModalProps {
  name: string
  image_url: string | undefined
  children: ReactNode
}

export default function ShareModal({
  name,
  image_url,
  children,
}: ShareModalProps) {
  const [url, setUrl] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrl(window.location.href)
    }
  }, [])

  const text = encodeURIComponent('Confira esta página incrível!')

  async function handleShare(platform: 'facebook' | 'linkedin' | 'whatsapp') {
    let shareUrl = ''
    const trackedUrl = encodeURIComponent(buildShareUrl(url, platform))
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${text}%20${trackedUrl}`
        break
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${trackedUrl}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${trackedUrl}`
        break
      default:
        break
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank')
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(buildShareUrl(url, 'copy_link'))
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="flex max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] w-[calc(100%-2rem)] max-w-lg flex-col gap-3 overflow-y-auto overscroll-contain rounded-lg px-3 pt-3 pb-4 [&>button]:hidden">
        <div className="flex shrink-0 items-center justify-between gap-3">
          <DialogTitle className="sr-only">Compartilhar curso</DialogTitle>
          <DialogClose className="ml-auto flex items-center gap-x-2 rounded-full bg-[#efefef] px-5 py-2 text-[#666666] text-sm">
            Fechar
            <X className="size-4" />
          </DialogClose>
        </div>

        <div className="relative shrink-0 overflow-hidden rounded-lg">
          <Image
            alt=""
            className="h-40 w-full object-cover brightness-50 sm:h-52"
            height={311}
            src={image_url || ''}
            width={464}
          />
          <span className="absolute right-4 bottom-4 left-4 line-clamp-2 text-white text-xl sm:text-2xl">
            {name}
          </span>
        </div>

        <p className="shrink-0 text-center text-[#777777] text-xs">
          Compartilhe com seus colegas de estudo. ;)
        </p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-x-2 sm:gap-y-4">
          <button
            className="group flex h-20 w-full flex-col items-center justify-center rounded-lg bg-primary-50 sm:h-24"
            onClick={handleCopy}
            type="button"
          >
            <Copy className="size-8 text-primary-700 group-hover:text-primary-400" />
            <span className="mt-2 text-[#6C757D] text-sm">
              {copied ? 'Link copiado!' : 'Copiar link'}
            </span>
          </button>

          <button
            className="group flex h-20 w-full flex-col items-center justify-center rounded-lg bg-primary-50 sm:h-24"
            onClick={() => handleShare('whatsapp')}
            type="button"
          >
            <svg
              className="size-8"
              fill="none"
              viewBox="0 0 18 18"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>WhatsApp</title>
              <path
                className="fill-primary-700 group-hover:fill-primary-400"
                d="M.316 18H0v-.317c.021-.047.047-.093.064-.142.459-1.352.912-2.706 1.379-4.056a.603.603 0 00-.05-.54 8.555 8.555 0 01-1.03-5.034c.152-1.592.69-3.054 1.67-4.32C4.146.86 6.945-.328 10.37.079c2.08.247 3.85 1.19 5.272 2.73 1.783 1.931 2.555 4.227 2.314 6.843a8.263 8.263 0 01-1.66 4.288c-1.681 2.225-3.928 3.43-6.709 3.594-1.562.092-3.05-.24-4.448-.951a.4.4 0 00-.332-.027c-.946.306-1.895.604-2.843.908-.55.176-1.099.358-1.649.537zm2.458-2.727c.073-.02.123-.03.171-.046.674-.213 1.351-.423 2.023-.645a.495.495 0 01.462.055 6.827 6.827 0 002.869 1.024 6.935 6.935 0 003.785-.587 6.899 6.899 0 002.85-2.383 6.777 6.777 0 001.2-3.279 6.758 6.758 0 00-.744-3.809 6.863 6.863 0 00-2.948-2.973 6.878 6.878 0 00-3.903-.787 6.892 6.892 0 00-3.334 1.195 6.867 6.867 0 00-2.667 3.483 6.779 6.779 0 00-.16 3.962c.204.809.548 1.556 1.034 2.233a.472.472 0 01.062.493c-.073.184-.132.375-.196.564l-.505 1.498.001.002z"
              />
              <path
                className="fill-primary-700 group-hover:fill-primary-400"
                d="M11.572 12.985c-.506-.001-.993-.144-1.482-.291-1.954-.59-3.338-1.9-4.472-3.532-.398-.573-.744-1.168-.85-1.868-.105-.693.005-1.35.395-1.95.258-.397.645-.56 1.086-.637.132-.023.266-.038.4-.054.283-.035.505.075.62.329.27.603.527 1.212.777 1.824.111.272-.033.506-.18.718-.126.182-.286.34-.42.518-.031.042-.048.139-.022.175.71 1.007 1.565 1.848 2.715 2.347.124.054.197.04.28-.07.176-.233.368-.455.551-.683.248-.31.446-.363.813-.2.331.146.659.301.987.458.238.113.47.235.707.35.194.095.278.246.288.462.063 1.33-1.022 2-1.955 2.089-.075.007-.151.01-.238.015z"
              />
            </svg>
            <span className="mt-2 text-[#6C757D] text-sm">Whatsapp</span>
          </button>

          <button
            className="group flex h-20 w-full flex-col items-center justify-center rounded-lg bg-primary-50 sm:h-24"
            onClick={() => handleShare('facebook')}
            type="button"
          >
            <svg
              className="size-8"
              fill="none"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>Facebook</title>
              <g clipPath="url(#clip0_share_facebook)">
                <path
                  className="fill-primary-700 group-hover:fill-primary-400"
                  d="M16 8.04902C16 3.60302 12.418 -0.000976562 8.00005 -0.000976562C3.58005 2.34375e-05 -0.00195312 3.60302 -0.00195312 8.05002C-0.00195312 12.067 2.92405 15.397 6.74805 16.001V10.376H4.71805V8.05002H6.75005V6.27502C6.75005 4.25802 7.94505 3.14402 9.77205 3.14402C10.648 3.14402 11.563 3.30102 11.563 3.30102V5.28102H10.554C9.56105 5.28102 9.25105 5.90202 9.25105 6.53902V8.04902H11.469L11.115 10.375H9.25005V16C13.074 15.396 16 12.066 16 8.04902Z"
                />
              </g>
              <defs>
                <clipPath id="clip0_share_facebook">
                  <rect fill="white" height="16" width="16" />
                </clipPath>
              </defs>
            </svg>

            <span className="mt-2 text-[#6C757D] text-sm">Facebook</span>
          </button>

          <button
            className="group flex h-20 w-full flex-col items-center justify-center rounded-lg bg-primary-50 sm:h-24"
            onClick={() => handleShare('linkedin')}
            type="button"
          >
            <svg
              className="size-8"
              fill="none"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>LinkedIn</title>
              <g clipPath="url(#clip0_share_linkedin)">
                <path
                  className="fill-primary-700 group-hover:fill-primary-400"
                  d="M0 1.146C0 0.513 0.526 0 1.175 0H14.825C15.474 0 16 0.513 16 1.146V14.854C16 15.487 15.474 16 14.825 16H1.175C0.526 16 0 15.487 0 14.854V1.146ZM4.943 13.394V6.169H2.542V13.394H4.943ZM3.743 5.182C4.58 5.182 5.101 4.628 5.101 3.934C5.086 3.225 4.581 2.686 3.759 2.686C2.937 2.686 2.4 3.226 2.4 3.934C2.4 4.628 2.921 5.182 3.727 5.182H3.743ZM8.651 13.394V9.359C8.651 9.143 8.667 8.927 8.731 8.773C8.904 8.342 9.299 7.895 9.963 7.895C10.832 7.895 11.179 8.557 11.179 9.529V13.394H13.58V9.25C13.58 7.03 12.396 5.998 10.816 5.998C9.542 5.998 8.971 6.698 8.651 7.191V7.216H8.635L8.651 7.191V6.169H6.251C6.281 6.847 6.251 13.394 6.251 13.394H8.651Z"
                />
              </g>
              <defs>
                <clipPath id="clip0_share_linkedin">
                  <rect fill="white" height="16" width="16" />
                </clipPath>
              </defs>
            </svg>

            <span className="mt-2 text-[#6C757D] text-sm">Linkedin</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
